import React, { useEffect, useState, useRef, useCallback } from "react";
import { CircularProgress, Box, Typography } from "@mui/material";
import FindListItem from "./FindListItem";
import { supabase } from "../supabaseClient";

const PAGE_SIZE = 10;

const FindList = ({ categoryId, isAdmin }) => {
  const [places, setPlaces] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const observer = useRef();

  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    const fetchSessionUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session && isMounted.current) {
        setUser(session.user);
      }
    };
    fetchSessionUser();
    return () => {
      isMounted.current = false;
    };
  }, []);

  const fetchPlaces = useCallback(async () => {
    setLoading(true);

    let query = supabase
      .from("places")
      .select("*", { count: "exact" })
      .range((page - 1) * PAGE_SIZE, page * PAGE_SIZE - 1);

    if (categoryId !== "all") {
      query = query.eq("category_id", categoryId);
    }

    const { data, error, count  } = await query;
    if (error) {
      console.error("Error fetching places:", error);
    } else {
      setPlaces((prev) => [...prev, ...data]);
      setTotalCount(count || 0); // ✅ 전체 개수 저장
      if (data.length < PAGE_SIZE) {
        setHasMore(false);
      }
    }
    setLoading(false);
 }, [page, categoryId]);

  useEffect(() => {
    // categoryId가 바뀌면 초기화
    setPlaces([]);
    setPage(1);
    setHasMore(true);
  }, [categoryId]);

  useEffect(() => {
    fetchPlaces();
  }, [fetchPlaces]);

  const lastItemRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prev) => prev + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore]
  );

  return (
    <Box sx={{ maxWidth: 600, mx: "auto", pt: 1 }}>
      <Box sx={{ display: "flex", gap: 1, justifyContent: "space-between", mb: 1 }}>
        <Typography>TOTAL : {totalCount}</Typography>
      </Box>

      {places.length > 0 ? (
        places.map((place, index) => (
          <div
            key={place.id}
            ref={index === places.length - 1 ? lastItemRef : null}
          >
            <FindListItem {...place} userId={user?.id} isAdmin={isAdmin} />
          </div>
        ))
      ) : (
        !loading && <Typography textAlign="center">데이터가 없습니다.</Typography>
      )}

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
};

export default FindList;
